import { useState, useRef, useEffect } from "react";
import { Controller } from "react-hook-form";
import { StyledInput, DarkInputBox, DarkTextInput, SelectMenu, SelectOption } from "./styles";

const ROLE_SUGGESTIONS = [
  // Sales
  "Account Director",
  "Account Executive",
  "Account Manager",
  "Area Sales Manager",
  "Business Development Director",
  "Business Development Executive",
  "Business Development Manager",
  "Business Development Representative",
  "Channel Account Manager",
  "Channel Partner Manager",
  "Channel Sales Manager",
  "Chief Revenue Officer",
  "Chief Sales Officer",
  "Commercial Director",
  "Commercial Manager",
  "Corporate Account Manager",
  "Director of Business Development",
  "Director of Sales",
  "District Sales Manager",
  "Enterprise Account Executive",
  "Enterprise Account Manager",
  "Enterprise Sales Manager",
  "Field Sales Manager",
  "Field Sales Representative",
  "Global Account Manager",
  "Global Sales Director",
  "Head of Sales",
  "Inside Sales Manager",
  "Inside Sales Representative",
  "Junior Sales Executive",
  "Key Account Director",
  "Key Account Manager",
  "National Account Manager",
  "National Sales Manager",
  "New Business Development Manager",
  "Outside Sales Representative",
  "Presales Consultant",
  "Presales Engineer",
  "Principal Account Executive",
  "Regional Account Manager",
  "Regional Director of Sales",
  "Regional Sales Manager",
  "Relationship Manager",
  "Sales Administrator",
  "Sales Analyst",
  "Sales Coach",
  "Sales Consultant",
  "Sales Coordinator",
  "Sales Development Manager",
  "Sales Development Representative",
  "Sales Director",
  "Sales Engineer",
  "Sales Executive",
  "Sales Manager",
  "Sales Operations Analyst",
  "Sales Operations Manager",
  "Sales Representative",
  "Sales Specialist",
  "Sales Support Specialist",
  "Sales Team Lead",
  "Sales Trainer",
  "Senior Account Director",
  "Senior Account Executive",
  "Senior Account Manager",
  "Senior Business Development Manager",
  "Senior Sales Consultant",
  "Senior Sales Executive",
  "Senior Sales Manager",
  "Senior Sales Representative",
  "Solution Sales Specialist",
  "Solutions Consultant",
  "Strategic Account Director",
  "Strategic Account Executive",
  "Strategic Account Manager",
  "Technical Sales Engineer",
  "Technical Sales Manager",
  "Technical Sales Representative",
  "Territory Manager",
  "Territory Sales Manager",
  "Territory Sales Representative",
  "VP of Business Development",
  "VP of Sales",
  "VP of Sales & Marketing",
  // Marketing
  "Brand Manager",
  "Chief Marketing Officer",
  "Content Manager",
  "Content Marketing Manager",
  "Content Strategist",
  "Demand Generation Manager",
  "Digital Marketing Manager",
  "Director of Marketing",
  "Growth Marketing Manager",
  "Head of Marketing",
  "Marketing Analyst",
  "Marketing Communications Manager",
  "Marketing Consultant",
  "Marketing Director",
  "Marketing Executive",
  "Marketing Manager",
  "Marketing Operations Manager",
  "Marketing Specialist",
  "Performance Marketing Manager",
  "Product Marketing Manager",
  "SEO Manager",
  "Social Media Manager",
  "VP of Marketing",
  // Customer Success & Support
  "Client Engagement Manager",
  "Client Relationship Manager",
  "Client Services Director",
  "Client Services Manager",
  "Customer Experience Manager",
  "Customer Relations Manager",
  "Customer Success Director",
  "Customer Success Manager",
  "Customer Success Team Lead",
  "Customer Support Manager",
  "Head of Customer Experience",
  "Head of Customer Success",
  "Implementation Manager",
  "Onboarding Manager",
  "Onboarding Specialist",
  "Partner Success Manager",
  "Renewals Manager",
  "Retention Manager",
  "Senior Customer Success Manager",
  // HR, L&D & People
  "Chief People Officer",
  "Compensation & Benefits Manager",
  "Employee Engagement Manager",
  "Head of HR",
  "Head of Learning & Development",
  "Head of People",
  "Head of Talent",
  "HR Advisor",
  "HR Business Partner",
  "HR Coordinator",
  "HR Director",
  "HR Generalist",
  "HR Manager",
  "HR Operations Manager",
  "Instructional Designer",
  "L&D Business Partner",
  "L&D Consultant",
  "L&D Manager",
  "L&D Specialist",
  "Learning & Development Consultant",
  "Learning & Development Director",
  "Learning & Development Manager",
  "Learning & Development Specialist",
  "Organisational Development Manager",
  "People Analytics Manager",
  "People Business Partner",
  "People Operations Manager",
  "People Partner",
  "Recruiter",
  "Senior HR Business Partner",
  "Senior HR Manager",
  "Talent Acquisition Manager",
  "Talent Acquisition Specialist",
  "Talent Development Manager",
  "Talent Management Manager",
  "Training & Development Manager",
  "Training & Development Specialist",
  "Training Coordinator",
  "Training Manager",
  "Workforce Development Manager",
  // Leadership & Executive
  "Chairman",
  "Chief Executive Officer",
  "Chief Financial Officer",
  "Chief Information Officer",
  "Chief Operating Officer",
  "Chief Technology Officer",
  "Co-Founder",
  "Director",
  "Director of Operations",
  "Division Manager",
  "Executive Director",
  "Executive Vice President",
  "Founder",
  "General Manager",
  "Head of Department",
  "Managing Director",
  "Operations Director",
  "Operations Manager",
  "Partner",
  "President",
  "Principal",
  "Regional Director",
  "Senior Director",
  "Senior Manager",
  "Senior Vice President",
  "Team Lead",
  "Team Manager",
  "Vice President",
  // Finance & Accounting
  "Accountant",
  "Chief Financial Officer",
  "Controller",
  "Finance Director",
  "Finance Manager",
  "Financial Analyst",
  "Financial Controller",
  "Head of Finance",
  "Treasury Manager",
  // Technology & Engineering
  "Backend Engineer",
  "Chief Technology Officer",
  "Data Analyst",
  "Data Engineer",
  "Data Scientist",
  "DevOps Engineer",
  "Engineering Manager",
  "Frontend Engineer",
  "Full Stack Engineer",
  "Head of Engineering",
  "IT Manager",
  "Machine Learning Engineer",
  "QA Engineer",
  "Security Engineer",
  "Software Engineer",
  "Solutions Architect",
  "Systems Administrator",
  "Technical Lead",
  "VP of Engineering",
  // Product
  "Chief Product Officer",
  "Head of Product",
  "Principal Product Manager",
  "Product Designer",
  "Product Director",
  "Product Manager",
  "Senior Product Manager",
  "VP of Product",
  // Consulting & Advisory
  "Associate Consultant",
  "Business Analyst",
  "Business Consultant",
  "Change Management Consultant",
  "Consultant",
  "Management Consultant",
  "Principal Consultant",
  "Senior Consultant",
  "Senior Manager (Consulting)",
  "Strategy Consultant",
  // Project & Programme Management
  "Programme Director",
  "Programme Manager",
  "Project Coordinator",
  "Project Director",
  "Project Manager",
  "Senior Project Manager",
  // Healthcare
  "Clinical Manager",
  "Healthcare Consultant",
  "Medical Director",
  "Practice Manager",
  // Legal
  "General Counsel",
  "Head of Legal",
  "In-House Counsel",
  "Legal Counsel",
  "Legal Manager",
  // Education
  "Academic Director",
  "Dean",
  "Head of Department (Education)",
  "Lecturer",
  "Professor",
  "Teacher",
  // Other
  "Administrator",
  "Business Owner",
  "Entrepreneur",
  "Freelancer / Contractor",
  "Graduate / Trainee",
  "Office Manager",
  "Operations Analyst",
  "Researcher",
  "Student",
];

interface Props {
  id: string;
  label: string;
  control: any;
  error?: string;
  dark?: boolean;
}

const RoleAutocompleteInput = ({ id, label, control, error, dark = false }: Props) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Controller
      name={id}
      control={control}
      defaultValue=""
      rules={{ required: "Job role is required" }}
      render={({ field: { onChange, value } }) => {
        const filtered = value
          ? ROLE_SUGGESTIONS.filter((r) =>
              r.toLowerCase().includes(value.toLowerCase())
            ).slice(0, 6)
          : ROLE_SUGGESTIONS.slice(0, 6);

        const showSuggestions = open && filtered.length > 0;

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (!showSuggestions) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && highlighted >= 0) {
            e.preventDefault();
            onChange(filtered[highlighted]);
            setOpen(false);
            setHighlighted(-1);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        };

        return (
          <div
            className={`input-field text-field${dark ? " dark-mode" : ""}`}
            ref={containerRef}
            style={{ position: "relative" }}
          >
            <label htmlFor={id}>{label}</label>
            {dark ? (
              <DarkInputBox className="dark-input-box" $focused={focused} $error={!!error}>
                <DarkTextInput
                  id={id}
                  type="text"
                  placeholder={label}
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(e.target.value);
                    setOpen(true);
                    setHighlighted(-1);
                  }}
                  onFocus={() => { setOpen(true); setFocused(true); }}
                  onBlur={() => setFocused(false)}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                />
              </DarkInputBox>
            ) : (
              <StyledInput
                id={id}
                type="text"
                placeholder={label}
                className={error ? "invalid-input" : ""}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value);
                  setOpen(true);
                  setHighlighted(-1);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />
            )}
            {error && <span className="form-error">{error}</span>}
            {showSuggestions && (
              <SelectMenu>
                {filtered.map((role, i) => (
                  <SelectOption
                    key={role}
                    $isSelected={role === value}
                    $isHovered={i === highlighted}
                    onMouseDown={(e: React.MouseEvent) => {
                      e.preventDefault();
                      onChange(role);
                      setOpen(false);
                      setHighlighted(-1);
                    }}
                    onMouseEnter={() => setHighlighted(i)}
                    onMouseLeave={() => setHighlighted(-1)}
                  >
                    {role}
                  </SelectOption>
                ))}
              </SelectMenu>
            )}
          </div>
        );
      }}
    />
  );
};

export default RoleAutocompleteInput;
